<template>
  <OperationFormLayoutSkeleton v-if="loading" />
  <OperationFormLayout
    v-if="!loading"
    :id="shaftId"
    :formSaving="saving"
    operationType="shaft"
    @cancel="$router.back()"
    @update="save"
  >
    <!-- Details section -->
    <OperationFormSection
      :modifiers="['border-radius-top']"
      :sectionDescription="$t('operations.details_description')"
      :sectionTitle="$t('operations.details_title')"
    >
      <FormRow
        :errors="errors?.shaft?.name"
        :label="$t('operations.operation_name_label_shaft')"
        labelFor="shaft_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors?.shaft?.name }">
            <input
              id="shaft_name"
              v-model="shaft.name"
              @input="validateInput('name')"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="$t('operations.operation_division_label')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <DivisionSelect
            v-model="shaft.division"
            :divisions="divisions"
          />
        </div>
      </FormRow>
      <FormRow
        v-if="shaft.division && shaft.division.isNew"
        :errors="divisionNameErrors"
        :label="$t('operations.operation_new_division_label')"
        labelFor="new_division_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': divisionNameErrors }">
            <input
              id="new_division_name"
              v-model="shaft.division.name"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>

    <!-- Dimensions section -->
    <OperationFormSection
      :sectionDescription="$t('shafts.configuration.shaft.type.description')"
      :sectionTitle="$t('shafts.configuration.shaft.type.title')"
    >
      <div>
        <FormRow
          :errors="errors?.shaft?.shape && !shaft?.shape ? errors?.shaft?.shape : []"
          :label="$t('shafts.select_shaft')"
          labelFor="shaft_shape"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <ShaftShape
              id="shaft_shape"
              v-model="shaft.shape"
              :class="{ 'shape--error': errors?.shaft?.shape && !shaft?.shape }"
              :iconList="shapeIcons"
            />
          </div>
        </FormRow>
      </div>
    </OperationFormSection>

    <OperationFormSection
      :modifiers="['border-radius-bottom']"
      :sectionDescription="$t('operations.shaft_detail_description')"
      :sectionTitle="$t('operations.dimensions_title')"
    >
      <FormRow
        v-if="shaft.shape === 'circle'"
        :errors="errors?.shaft?.diameter"
        :label="$t('shafts.configuration.shaft.dimensions.diameter')"
        labelFor="diameter"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div
            :class="{ input: true, 'input--error': errors?.shaft?.diameter }"
          >
            <input
              id="diameter"
              v-model="shaft.dimensions.diameter"
              @input="validateInput('diameter')"
            >
          </div>
        </div>
      </FormRow>

      <template v-else>
        <FormRow
          :errors="errors?.shaft?.width"
          :label="$t('shafts.configuration.shaft.dimensions.width')"
          labelFor="width"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div
              :class="{ input: true, 'input--error': errors?.shaft?.width }"
            >
              <input
                id="width"
                v-model="shaft.dimensions.width"
                @input="validateInput('width')"
              >
            </div>
          </div>
        </FormRow>

        <FormRow
          :errors="errors?.shaft?.length"
          :label="$t('shafts.configuration.shaft.dimensions.length')"
          labelFor="length"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div
              :class="{ input: true, 'input--error': errors?.shaft?.length }"
            >
              <input
                id="length"
                v-model="shaft.dimensions.length"
                @input="validateInput('length')"
              >
            </div>
          </div>
        </FormRow>
      </template>

      <FormRow
        :errors="errors?.shaft?.set_spacing"
        :label="'Set spacing'"
        labelFor="set_spacing"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors?.shaft?.set_spacing }">
            <input
              id="set_spacing"
              v-model="shaft.set_spacing"
              @input="validateInput('set_spacing')"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :errors="errors?.shaft?.number_of_sets"
        :label="$t('shafts.number_of_sets')"
        labelFor="number_of_sets"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div
            :class="{
              input: true,
              'input--error': errors?.shaft?.number_of_sets,
            }"
          >
            <input
              id="number_of_sets"
              v-model="shaft.number_of_sets"
              @input="validateInput('number_of_sets')"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';

// Utils
import dig from '@/functions/dig';
import useAjax from '@/functions/use_ajax';
import omitObjectItem from '@/functions/omit_object_item';
import { useRouter } from 'vue-router';

// Components
import FormRow from '@/components/form/row.vue';
import DivisionSelect from '@/components/operations/division_select.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import ShaftShape from '@/components/form/shaft_shape.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();
const divisions = ref(null);
const initialShaft = ref(null);
const shaft = reactive(createNewShaft());
const errors = ref(null);
const saving = ref(false);
const loading = ref(false);

/**
 * Shape list
 */
const shapeIcons = ref(
  [
    'circle',
    'rectangle',
  ]
);

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${props.organisationId}/divisions`,
  shafts: `/organisations/${props.organisationId}/shafts_v2/shafts`,
  shaft: `/organisations/${props.organisationId}/shafts_v2/shafts/${props.shaftId}`,
  get post_or_patch() {
    return props.shaftId ? this.shaft : this.shafts;
  }
};

onMounted(() => {
  loadDivisions();
  loadShaft();
});

const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);

async function loadShaft() {
  if (props.shaftId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.shaft);

    if (ok) {
      initialShaft.value = data;
      Object.assign(shaft, data);
      loading.value = false;
    }
  } else {
    initialShaft.value = createNewShaft();
  }
}

async function loadDivisions() {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
}

function createNewShaft() {
  return {
    name: '',
    division: null,
    set_spacing: null,
    number_of_sets: null,
    shape: 'circle',
    dimensions: {
      diameter: null,
      width: null,
      length: null,
    },
  };
}

function formatDivisionAttributes() {
  if (shaft.division?.isNew) {
    return {
      division_id: 'new',
      new_division: shaft.division.name,
    };
  } else {
    return {
      division_id: shaft.division?.id || null,
    };
  }
}

function shaftDimensions() {
  if (shaft.shape === 'circle') return omitObjectItem(['width', 'length'], shaft.dimensions);
  return omitObjectItem(['diameter'], shaft.dimensions);
}

const formatShaftForPost = () => {
  return {
    shaft: {
      name: shaft.name,
      shape: shaft.shape,
      dimensions: shaftDimensions(),
      set_spacing: shaft.set_spacing,
      number_of_sets: shaft.number_of_sets,
      ...formatDivisionAttributes(),
    },
  };
};

async function save() {
  errors.value = null;
  saving.value = true;

  let method = props.shaftId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: formatShaftForPost(),
  });

  if (ok) {
    router.push({ name: 'shaftV2', params: { shaftId: data.id } });
  } else {
    errors.value = data;
  }

  saving.value = false;
}

function validateInput(field) {
  if (errors.value) {
    errors.value.shaft[field] = null;
  }
}
</script>
